import {
  useQuery,
  useMutation,
  useQueryClient,
  UseMutationResult,
} from "@tanstack/react-query";
import { getAllProducts } from "@/common/services/product.api";
import { getWorks } from "@/common/services/work.api";
import { IProduct } from "@/types/product.types";
import { IWork } from "@/types/work.type";
import {
  getInvoiceOperation,
  createInvoiceOperation,
  emitInvoiceOperation,
  updateInvoiceOperation,
  cancellationInvoiceOperation,
} from "@/common/services/invoice-operation.api";
import { IInvoiceOperation } from "@/types/invoice-operation.type";
import { useAppStore } from "@/common/states/store";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { useCompanyStore } from "@/common/states/store/Company";
import { sendDataIleva } from "@/modules/renters/services/integration.api";
import { getAllPurchaseRequestors } from "@/common/services/purchaseRequestor.api";

export const useGetProducts = () => {
  return useQuery<IProduct[], Error>({
    queryKey: ["products"],
    queryFn: async () => {
      const response = await getAllProducts();
      if (!response.data) {
        throw new Error("Erro ao buscar produtos");
      }
      return response.data;
    },
  });
};

export const useGetServices = () => {
  return useQuery<IWork[], Error>({
    queryKey: ["services"],
    queryFn: async () => {
      const response = await getWorks();
      if (!response.ok) {
        throw new Error("Erro ao buscar serviços");
      }
      return response.data;
    },
  });
};

export const useGetInvoiceOperation = () => {
  const {
    invoice: { filters, page },
  } = useAppStore();

  const companyStore = useCompanyStore();
  const { data, isError, isFetching, isSuccess, isLoading, error, refetch } =
    useQuery<
      {
        data: IInvoiceOperation[];
        ok: boolean;
        totalItems: number;
      },
      Error
    >({
      queryKey: ["invoiceOperations", filters, page, companyStore],
      queryFn: async () => {
        const response = await getInvoiceOperation({ ...filters, page });
        if (!response.data) {
          throw new Error("Erro ao buscar notas fiscais");
        }
        return response;
      },
      enabled: !!companyStore.company,
    });

  return {
    data,
    isError,
    isFetching,
    isSuccess,
    isLoading,
    error,
    refetch,
  };
};

type CreateInvoiceOperationInput = Omit<
  IInvoiceOperation,
  "_id" | "companyId" | "dateEmission"
>;

export const useCreateInvoiceOperation = () => {
  const queryClient = useQueryClient();

  return useMutation<IInvoiceOperation, Error, CreateInvoiceOperationInput>({
    mutationFn: async (invoiceData) => {
      const response = await createInvoiceOperation(invoiceData);
      if (!response.ok || !response.data) {
        throw new Error("Erro ao criar nota fiscal");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoiceOperations"] });
    },
  });
};

export const useUpadateInvoiceOperation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      values,
    }: {
      id: string;
      values: Omit<IInvoiceOperation, "companyId" | "dateEmission">;
    }) => {
      const response = await updateInvoiceOperation(id, values);
      if (!response.ok) {
        throw new Error("Erro ao atualizar nota fiscal");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoiceOperations"] });
    },
  });
};

export const useEmitInvoiceOperation = () => {
  const queryClient = useQueryClient();

  const { notifySuccess, notifyError } = useToastContext();

  return useMutation<IInvoiceOperation, Error, string>({
    mutationFn: async (id: string) => {
      const response = await emitInvoiceOperation(id);
      if (!response.ok || !response.data) {
        notifyError(response.message || "Erro ao emitir nota fiscal");
        throw new Error("Erro ao emitir nota fiscal");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoiceOperations"] });
      notifySuccess("Nota fiscal enviada para processamento!");
    },
  });
};

export const useCancelInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const response = await cancellationInvoiceOperation(id, reason);
      if (!response.ok || !response.data) {
        throw new Error("Erro ao processar pedido de cancelamento");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoiceOperations"] });
    },
  });
};

export const useGetAllPurchaseRequestors = () => {
  return useQuery({
    queryKey: ["all-purchase-requestors"],
    queryFn: async () => {
      const response = await getAllPurchaseRequestors();
      if (!response.ok) {
        throw new Error("Erro ao buscar requisitores");
      }
      return response.data;
    },
  });
};

type ApiResponse = { ok: boolean };

export const useApproveIleva = (
  vehicleId: string
): UseMutationResult<ApiResponse, Error, void> => {
  const { notifySuccess, notifyError } = useToastContext();

  return useMutation<ApiResponse, Error, void>({
    mutationFn: async () => {
      const result = await sendDataIleva({ vehicleId });
      if (!result.ok) {
        throw new Error("Erro ao enviar dados para Ileva");
      }
      return result;
    },
    onError: (error) => {
      notifyError(error.message);
    },
    onSuccess: () => {
      notifySuccess("Dados enviados para o Ileva com sucesso!");
    },
  });
};
